import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;
const SUPPLIER_SEARCH_MIN_LENGTH = 3;
const SUPPLIER_SEARCH_DEBOUNCE_MS = 400;

const CUSTOMER_SEARCH_MIN_LENGTH = 3;
const CUSTOMER_SEARCH_DEBOUNCE_MS = 400;

function getSupplierId(supplier) {
  return String(supplier?.id || supplier?._id || "").trim();
}

function getSupplierLabel(supplier) {
  return String(supplier?.clientname || supplier?.name || "").trim();
}

function getCustomerId(customer) {
  return String(customer?.id || customer?._id || "").trim();
}

function getCustomerLabel(customer) {
  return String(customer?.customerName || customer?.name || "").trim();
}

function getCustomerContacts(customer) {
  if (!customer) return [];
  if (Array.isArray(customer.contacts) && customer.contacts.length > 0) {
    return customer.contacts;
  }
  return [];
}

function buildCustomerContactDraft(contact) {
  return {
    name: String(contact?.contactPerson || "").trim(),
    email: String(contact?.email || "").trim(),
    phoneNumber: normalizePhoneDigits(contact?.contactNumber),
  };
}

function normalizePhoneDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length <= 10) return digits;
  return digits.slice(-10);
}

function getSupplierContacts(supplier) {
  if (!supplier) return [];
  if (Array.isArray(supplier.contacts) && supplier.contacts.length > 0) {
    return supplier.contacts;
  }
  if (supplier.contactPerson || supplier.email || supplier.phonenumber) {
    return [
      {
        _id: "primary",
        contactPerson: supplier.contactPerson || "",
        email: supplier.email || "",
        phonenumber: supplier.phonenumber || "",
      },
    ];
  }
  return [];
}

function getContactKey(contact, index) {
  return contact._id != null ? String(contact._id) : `idx-${index}`;
}

function parseApiResponse(raw) {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const s = responseData.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function buildContactDraft(contact) {
  return {
    name: String(contact?.contactPerson || "").trim(),
    email: String(contact?.email || "").trim(),
    phoneNumber: normalizePhoneDigits(contact?.phonenumber),
  };
}

const UserAdd = () => {
  const navigate = useNavigate();

  const currentRole = String(localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();
  const isSuperAdmin = currentRole === "superadmin";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);
  const [isFetchingRoles, setIsFetchingRoles] = useState(!isSuperAdmin);

  const [businessId, setBusinessId] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [isFetchingBusinesses, setIsFetchingBusinesses] =
    useState(isSuperAdmin);

  const [isLoading, setIsLoading] = useState(false);

  const [supplierId, setSupplierId] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierSearchResults, setSupplierSearchResults] = useState([]);
  const [isFetchingSuppliers, setIsFetchingSuppliers] = useState(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [selectedContactKeys, setSelectedContactKeys] = useState(
    () => new Set(),
  );
  const [selectedContactDrafts, setSelectedContactDrafts] = useState({});

  const supplierSearchDebounceRef = useRef(null);
  const supplierSearchRequestIdRef = useRef(0);
  const supplierSearchPanelRef = useRef(null);

  const supplierContacts = getSupplierContacts(selectedSupplier);
  const useSupplierContactSelection =
    role === "supplier" && supplierContacts.length > 0;

  const [customerId, setCustomerId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomerContactKeys, setSelectedCustomerContactKeys] =
    useState(() => new Set());
  const [selectedCustomerContactDrafts, setSelectedCustomerContactDrafts] =
    useState({});

  const customerSearchDebounceRef = useRef(null);
  const customerSearchRequestIdRef = useRef(0);
  const customerSearchPanelRef = useRef(null);

  const customerContacts = getCustomerContacts(selectedCustomer);
  const useCustomerContactSelection =
    role === "customer" && customerContacts.length > 0;

  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchBusinesses = async () => {
      setIsFetchingBusinesses(true);
      try {
        const currentUserId = localStorage.getItem("id");
        const payload = {
          action: "get-suppliers",
          currentUserId,
          status: "active",
        };
        const response = await axios.post(API_URL, payload);

        let responseData = response.data;
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.data) {
          const list = responseData.data.businesses || [];
          setBusinesses(list);
        } else {
          toast.error(responseData.message || "Failed to fetch businesses", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch businesses error:", error);
        toast.error("Failed to load businesses", { autoClose: 2000 });
      } finally {
        setIsFetchingBusinesses(false);
      }
    };

    fetchBusinesses();
  }, [isSuperAdmin]);

  const searchSuppliers = useCallback(
    async (term, { showToast = false } = {}) => {
      const search = String(term || "").trim();
      if (search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
        setSupplierSearchResults([]);
        return;
      }

      const requestId = ++supplierSearchRequestIdRef.current;
      setIsFetchingSuppliers(true);

      try {
        const userRole = localStorage.getItem("role");
        // Same API as SupplyMaster — search only, no pagination
        const payload = {
          action: "get-suppliers",
          status: "active",
          search,
        };
        if (userRole !== "admin") {
          payload.status = "active";
        }

        const response = await axios.post(API_URL, payload);
        if (requestId !== supplierSearchRequestIdRef.current) return;

        const responseData = parseApiResponse(response.data);
        const list = Array.isArray(responseData?.suppliers)
          ? responseData.suppliers
          : Array.isArray(responseData?.data?.suppliers)
            ? responseData.data.suppliers
            : [];

        const uniqueSuppliers = list.filter(
          (supplier, index, self) =>
            index ===
            self.findIndex((s) => getSupplierId(s) === getSupplierId(supplier)),
        );

        if (isApiSuccess(responseData) && uniqueSuppliers.length > 0) {
          setSupplierSearchResults(uniqueSuppliers);
          setShowSupplierDropdown(true);
          if (showToast) {
            toast.info(`${uniqueSuppliers.length} supplier(s) found.`, {
              autoClose: 2500,
            });
          }
        } else {
          setSupplierSearchResults([]);
          setShowSupplierDropdown(true);
          if (showToast) {
            toast.info(responseData?.message || "No suppliers found", {
              autoClose: 2000,
            });
          }
        }
      } catch (error) {
        if (requestId !== supplierSearchRequestIdRef.current) return;
        console.error("Supplier search error:", error);
        toast.error("Failed to search suppliers", { autoClose: 2000 });
        setSupplierSearchResults([]);
      } finally {
        if (requestId === supplierSearchRequestIdRef.current) {
          setIsFetchingSuppliers(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        supplierSearchPanelRef.current &&
        !supplierSearchPanelRef.current.contains(e.target)
      ) {
        setShowSupplierDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isSuperAdmin) return;

    const fetchRoles = async () => {
      setIsFetchingRoles(true);
      try {
        const payload = {
          action: "get-role",
          page: 1,
          limit: 100,
        };
        const response = await axios.post(API_URL, payload);

        let responseData = response.data;
        if (typeof responseData === "string") {
          try {
            responseData = JSON.parse(responseData);
          } catch (parseError) {
            responseData = response.data;
          }
        }

        if (responseData.success === true && responseData.data) {
          // Filter only active roles
          const activeRoles = responseData.data.filter(
            (r) => r.status === "active",
          );
          setRoles(activeRoles);

          // Set default role to first active role or 'user'
          if (activeRoles.length > 0) {
            const defaultRole = activeRoles.find(
              (r) => r.role.toLowerCase() === "user",
            );
            setRole(defaultRole ? defaultRole.role : activeRoles[0].role);
          }
        } else {
          toast.error(responseData.message || "Failed to fetch roles", {
            autoClose: 2000,
          });
        }
      } catch (error) {
        console.error("Fetch roles error:", error);
        toast.error("Failed to load roles", { autoClose: 2000 });
      } finally {
        setIsFetchingRoles(false);
      }
    };

    fetchRoles();
  }, [isSuperAdmin]);

  const searchCustomers = useCallback(
    async (term, { showToast = false } = {}) => {
      const search = String(term || "").trim();
      if (search.length < CUSTOMER_SEARCH_MIN_LENGTH) {
        setCustomerSearchResults([]);
        return;
      }

      const requestId = ++customerSearchRequestIdRef.current;
      setIsFetchingCustomers(true);

      try {
        const payload = {
          action: "get-all-customers",
          search,
        };

        const response = await axios.post(API_URL, payload);
        if (requestId !== customerSearchRequestIdRef.current) return;

        const responseData = parseApiResponse(response.data);
        const list = Array.isArray(responseData?.data?.customers)
          ? responseData.data.customers
          : Array.isArray(responseData?.customers)
            ? responseData.customers
            : [];

        const uniqueCustomers = list.filter(
          (customer, index, self) =>
            index ===
            self.findIndex((c) => getCustomerId(c) === getCustomerId(customer)),
        );

        if (isApiSuccess(responseData) && uniqueCustomers.length > 0) {
          setCustomerSearchResults(uniqueCustomers);
          setShowCustomerDropdown(true);
          if (showToast) {
            toast.info(`${uniqueCustomers.length} customer(s) found.`, {
              autoClose: 2500,
            });
          }
        } else {
          setCustomerSearchResults([]);
          setShowCustomerDropdown(true);
          if (showToast) {
            toast.info(responseData?.message || "No customers found", {
              autoClose: 2000,
            });
          }
        }
      } catch (error) {
        if (requestId !== customerSearchRequestIdRef.current) return;
        console.error("Customer search error:", error);
        toast.error("Failed to search customers", { autoClose: 2000 });
        setCustomerSearchResults([]);
      } finally {
        if (requestId === customerSearchRequestIdRef.current) {
          setIsFetchingCustomers(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (customerSearchDebounceRef.current) {
        clearTimeout(customerSearchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        customerSearchPanelRef.current &&
        !customerSearchPanelRef.current.contains(e.target)
      ) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clearInviteContactFields = () => {
    setName("");
    setEmail("");
    setPhoneNumber("");
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
    setSupplierId("");
    setSelectedSupplier(null);
    setSupplierSearch("");
    setSupplierSearchResults([]);
    setShowSupplierDropdown(false);
    setSelectedContactKeys(new Set());
    setSelectedContactDrafts({});
    setCustomerId("");
    setSelectedCustomer(null);
    setCustomerSearch("");
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    setSelectedCustomerContactKeys(new Set());
    setSelectedCustomerContactDrafts({});
    clearInviteContactFields();
  };

  const handleSupplierSearchChange = (e) => {
    const value = e.target.value;
    setSupplierSearch(value);
    supplierSearchRequestIdRef.current += 1;

    if (supplierId || selectedSupplier) {
      setSupplierId("");
      setSelectedSupplier(null);
      setSelectedContactKeys(new Set());
      setSelectedContactDrafts({});
      clearInviteContactFields();
    }

    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }

    const trimmed = value.trim();
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setIsFetchingSuppliers(false);
      setSupplierSearchResults([]);
      setShowSupplierDropdown(false);
      return;
    }

    supplierSearchDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed, { showToast: false });
    }, SUPPLIER_SEARCH_DEBOUNCE_MS);
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    customerSearchRequestIdRef.current += 1;

    if (customerId || selectedCustomer) {
      setCustomerId("");
      setSelectedCustomer(null);
      setSelectedCustomerContactKeys(new Set());
      setSelectedCustomerContactDrafts({});
      clearInviteContactFields();
    }

    if (customerSearchDebounceRef.current) {
      clearTimeout(customerSearchDebounceRef.current);
    }

    const trimmed = value.trim();
    if (trimmed.length < CUSTOMER_SEARCH_MIN_LENGTH) {
      setIsFetchingCustomers(false);
      setCustomerSearchResults([]);
      setShowCustomerDropdown(false);
      return;
    }

    customerSearchDebounceRef.current = setTimeout(() => {
      searchCustomers(trimmed, { showToast: false });
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
  };

  const handleCustomerSearchTrigger = () => {
    if (customerSearchDebounceRef.current) {
      clearTimeout(customerSearchDebounceRef.current);
    }
    searchCustomers(customerSearch, { showToast: true });
  };

  const handleCustomerSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCustomerSearchTrigger();
    }
  };

  const handleSelectCustomer = (customer) => {
    const cid = getCustomerId(customer);
    if (!cid) {
      toast.error("Selected customer is missing an ID", { autoClose: 2000 });
      return;
    }
    setCustomerId(cid);
    setSelectedCustomer(customer);
    setCustomerSearch(getCustomerLabel(customer));
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    setSelectedCustomerContactKeys(new Set());
    setSelectedCustomerContactDrafts({});
    clearInviteContactFields();
  };

  const handleCustomerContactToggle = (contactKey, contact, checked) => {
    setSelectedCustomerContactKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(contactKey);
      } else {
        next.delete(contactKey);
      }
      return next;
    });

    setSelectedCustomerContactDrafts((prev) => {
      const next = { ...prev };
      if (checked) {
        next[contactKey] = buildCustomerContactDraft(contact);
      } else {
        delete next[contactKey];
      }
      return next;
    });
  };

  const handleCustomerContactRowClick = (contactKey, contact, isSelected) => {
    if (!isSelected) {
      handleCustomerContactToggle(contactKey, contact, true);
    }
  };

  const handleCustomerContactDraftChange = (contactKey, field, value) => {
    setSelectedCustomerContactDrafts((prev) => ({
      ...prev,
      [contactKey]: {
        ...(prev[contactKey] || buildCustomerContactDraft({})),
        [field]: value,
      },
    }));
  };

  const getSelectedCustomerInviteEntries = () =>
    [...selectedCustomerContactKeys].map((contactKey) => ({
      contactKey,
      ...(selectedCustomerContactDrafts[contactKey] ||
        buildCustomerContactDraft({})),
    }));

  const sendCustomerInvite = async (currentUserId, invitePayload) => {
    const response = await axios.post(API_URL, invitePayload);
    const responseData = parseApiResponse(response.data);
    if (responseData?.success === true) {
      return { ok: true, message: responseData.message };
    }
    return {
      ok: false,
      message: responseData?.message || "Failed to invite user",
    };
  };

  const handleSupplierSearchTrigger = () => {
    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }
    searchSuppliers(supplierSearch, { showToast: true });
  };

  const handleSupplierSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSupplierSearchTrigger();
    }
  };

  const handleSelectSupplier = (supplier) => {
    const sid = getSupplierId(supplier);
    if (!sid) {
      toast.error("Selected supplier is missing an ID", { autoClose: 2000 });
      return;
    }
    setSupplierId(sid);
    setSelectedSupplier(supplier);
    setSupplierSearch(getSupplierLabel(supplier));
    setSupplierSearchResults([]);
    setShowSupplierDropdown(false);
    setSelectedContactKeys(new Set());
    setSelectedContactDrafts({});
    clearInviteContactFields();
  };

  const handleContactToggle = (contactKey, contact, checked) => {
    setSelectedContactKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(contactKey);
      } else {
        next.delete(contactKey);
      }
      return next;
    });

    setSelectedContactDrafts((prev) => {
      const next = { ...prev };
      if (checked) {
        next[contactKey] = buildContactDraft(contact);
      } else {
        delete next[contactKey];
      }
      return next;
    });
  };

  const handleContactRowClick = (contactKey, contact, isSelected) => {
    if (!isSelected) {
      handleContactToggle(contactKey, contact, true);
    }
  };

  const handleContactDraftChange = (contactKey, field, value) => {
    setSelectedContactDrafts((prev) => ({
      ...prev,
      [contactKey]: {
        ...(prev[contactKey] || buildContactDraft({})),
        [field]: value,
      },
    }));
  };

  const getSelectedInviteEntries = () =>
    [...selectedContactKeys].map((contactKey) => ({
      contactKey,
      ...(selectedContactDrafts[contactKey] || buildContactDraft({})),
    }));

  const validateInviteContact = (
    contactLabel,
    nameValue,
    emailValue,
    phoneValue,
  ) => {
    if (!nameValue.trim()) {
      toast.error(`Please enter a name for ${contactLabel}`, {
        autoClose: 2000,
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue.trim()) {
      toast.error(`Please enter an email for ${contactLabel}`, {
        autoClose: 2000,
      });
      return false;
    }
    if (!emailRegex.test(emailValue.trim())) {
      toast.error(`Please enter a valid email for ${contactLabel}`, {
        autoClose: 2000,
      });
      return false;
    }

    if (phoneValue.trim() && phoneValue.trim().length !== 10) {
      toast.error(
        `Phone number for ${contactLabel} must be exactly 10 digits`,
        {
          autoClose: 2000,
        },
      );
      return false;
    }

    return true;
  };

  const sendSupplierInvite = async (currentUserId, invitePayload) => {
    const response = await axios.post(API_URL, invitePayload);
    const responseData = parseApiResponse(response.data);
    if (responseData?.success === true) {
      return { ok: true, message: responseData.message };
    }
    return {
      ok: false,
      message: responseData?.message || "Failed to invite user",
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (isSuperAdmin) {
      if (!businessId) {
        toast.error("Please select a business", { autoClose: 2000 });
        return;
      }
    } else if (!role || role.trim() === "") {
      toast.error("Please select a role", { autoClose: 2000 });
      return;
    } else if (role === "supplier" && !supplierId) {
      toast.error("Please select a supplier", { autoClose: 2000 });
      return;
    } else if (role === "customer" && !customerId) {
      toast.error("Please select a customer", { autoClose: 2000 });
      return;
    }

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("User session expired. Please login again", {
        autoClose: 2000,
      });
      navigate("/");
      return;
    }

    if (role === "supplier" && useSupplierContactSelection) {
      const selectedEntries = getSelectedInviteEntries();
      if (selectedEntries.length === 0) {
        toast.error("Please select at least one contact", { autoClose: 2000 });
        return;
      }

      for (const entry of selectedEntries) {
        const contactLabel = entry.name || "contact";
        if (
          !validateInviteContact(
            contactLabel,
            entry.name,
            entry.email,
            entry.phoneNumber,
          )
        ) {
          return;
        }
      }

      setIsLoading(true);
      try {
        let successCount = 0;
        const failedMessages = [];

        for (const entry of selectedEntries) {
          const result = await sendSupplierInvite(currentUserId, {
            action: "invite-supplier",
            currentUserId,
            supplierId,
            email: String(entry.email || "").trim(),
            name: String(entry.name || "").trim(),
            phoneNumber: normalizePhoneDigits(entry.phoneNumber),
          });

          if (result.ok) {
            successCount += 1;
          } else {
            failedMessages.push(
              `${entry.name || entry.email}: ${result.message}`,
            );
          }
        }

        if (successCount === selectedEntries.length) {
          toast.success(
            successCount === 1
              ? "Invitation email sent successfully"
              : `${successCount} invitation emails sent successfully`,
            { autoClose: 3000 },
          );
          setTimeout(() => {
            navigate("/user");
          }, 1500);
        } else if (successCount > 0) {
          toast.warning(
            `${successCount} of ${selectedEntries.length} invites sent. ${failedMessages[0] || ""}`,
            { autoClose: 4000 },
          );
        } else {
          toast.error(failedMessages[0] || "Failed to invite users", {
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Invite supplier contacts error:", error);
        toast.error(
          error.response?.data?.message ||
            "An error occurred while inviting users",
          { autoClose: 2000 },
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (role === "customer" && useCustomerContactSelection) {
      const selectedEntries = getSelectedCustomerInviteEntries();
      if (selectedEntries.length === 0) {
        toast.error("Please select at least one contact", { autoClose: 2000 });
        return;
      }

      for (const entry of selectedEntries) {
        const contactLabel = entry.name || "contact";
        if (
          !validateInviteContact(
            contactLabel,
            entry.name,
            entry.email,
            entry.phoneNumber,
          )
        ) {
          return;
        }
      }

      setIsLoading(true);
      try {
        let successCount = 0;
        const failedMessages = [];

        for (const entry of selectedEntries) {
          const result = await sendCustomerInvite(currentUserId, {
            action: "invite-customer",
            currentUserId,
            customerId,
            email: String(entry.email || "").trim(),
            name: String(entry.name || "").trim(),
            phoneNumber: normalizePhoneDigits(entry.phoneNumber),
          });

          if (result.ok) {
            successCount += 1;
          } else {
            failedMessages.push(
              `${entry.name || entry.email}: ${result.message}`,
            );
          }
        }

        if (successCount === selectedEntries.length) {
          toast.success(
            successCount === 1
              ? "Invitation email sent successfully"
              : `${successCount} invitation emails sent successfully`,
            { autoClose: 3000 },
          );
          setTimeout(() => {
            navigate("/user");
          }, 1500);
        } else if (successCount > 0) {
          toast.warning(
            `${successCount} of ${selectedEntries.length} invites sent. ${failedMessages[0] || ""}`,
            { autoClose: 4000 },
          );
        } else {
          toast.error(failedMessages[0] || "Failed to invite users", {
            autoClose: 3000,
          });
        }
      } catch (error) {
        console.error("Invite customer contacts error:", error);
        toast.error(
          error.response?.data?.message ||
            "An error occurred while inviting users",
          { autoClose: 2000 },
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Validation for manual single invite
    if (!name.trim()) {
      toast.error("Please enter a name", { autoClose: 2000 });
      return;
    }

    if (!email.trim()) {
      toast.error("Please enter an email", { autoClose: 2000 });
      return;
    }

    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address", { autoClose: 2000 });
      return;
    }

    if (!phoneNumber.trim()) {
      toast.error("Please enter a phone number", { autoClose: 2000 });
      return;
    }

    if (phoneNumber.length !== 10) {
      toast.error("Phone number must be exactly 10 digits", {
        autoClose: 2000,
      });
      return;
    }

    setIsLoading(true);

    try {
      let payload;
      if (isSuperAdmin) {
        payload = {
          action: "invite-user",
          currentUserId,
          email: email.trim(),
          name: name.trim(),
          phoneNumber: phoneNumber.trim(),
          role: "admin",
          businessId,
        };
      } else if (role === "supplier") {
        payload = {
          action: "invite-supplier",
          currentUserId,
          supplierId,
          email: email.trim(),
          name: name.trim(),
          phoneNumber: phoneNumber.trim(),
        };
      } else if (role === "customer") {
        payload = {
          action: "invite-customer",
          currentUserId,
          customerId,
          email: email.trim(),
          name: name.trim(),
          phoneNumber: phoneNumber.trim(),
        };
      } else {
        payload = {
          action: "invite-user",
          currentUserId,
          email: email.trim(),
          name: name.trim(),
          phoneNumber: phoneNumber.trim(),
          role,
        };
      }

      const response = await axios.post(API_URL, payload);
      const responseData = parseApiResponse(response.data);

      if (responseData.success === true) {
        toast.success(
          responseData.message || "Invitation email sent successfully",
          { autoClose: 3000 },
        );
        setTimeout(() => {
          navigate("/user");
        }, 1500);
      } else {
        toast.error(responseData.message || "Failed to invite user", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Invite user error:", error);
      toast.error(
        error.response?.data?.message ||
          "An error occurred while inviting user",
        { autoClose: 2000 },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const selectedContactCount = selectedContactKeys.size;

  const selectedCustomerContactCount = selectedCustomerContactKeys.size;

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">
                {isSuperAdmin ? "Invite Tenant Admin" : "Invite User"}
              </h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/user">
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <form className="form-horizontal" onSubmit={handleSubmit}>
                    <div className="card-body">
                      {isSuperAdmin ? (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Business<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <select
                              className="form-control"
                              value={businessId}
                              onChange={(e) => setBusinessId(e.target.value)}
                              disabled={isLoading || isFetchingBusinesses}
                              required
                            >
                              <option value="">Select Business</option>
                              {businesses.map((b) => (
                                <option key={b._id} value={b._id}>
                                  {b.businessName}
                                </option>
                              ))}
                            </select>
                            <small className="form-text text-muted">
                              This user will be created as the Admin for the
                              selected business.
                            </small>
                          </div>
                        </div>
                      ) : (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Role<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <select
                              className="form-control"
                              value={role}
                              onChange={handleRoleChange}
                              disabled={isLoading || isFetchingRoles}
                              required
                            >
                              <option value="">Select Role</option>
                              {roles.map((roleItem) => (
                                <option key={roleItem.id} value={roleItem.role}>
                                  {roleItem.role.charAt(0).toUpperCase() +
                                    roleItem.role.slice(1)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                      {role === "supplier" && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Supplier<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <div
                              className="position-relative"
                              ref={supplierSearchPanelRef}
                            >
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search supplier by name..."
                                  value={supplierSearch}
                                  onChange={handleSupplierSearchChange}
                                  onKeyPress={handleSupplierSearchKeyPress}
                                  onFocus={() => {
                                    if (supplierSearchResults.length > 0) {
                                      setShowSupplierDropdown(true);
                                    }
                                  }}
                                  disabled={isLoading}
                                  autoComplete="off"
                                  required={!supplierId}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleSupplierSearchTrigger}
                                    disabled={isLoading || isFetchingSuppliers}
                                  >
                                    {isFetchingSuppliers ? (
                                      <i className="fa-solid fa-spinner fa-spin" />
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {showSupplierDropdown &&
                                supplierSearch.trim().length >=
                                  SUPPLIER_SEARCH_MIN_LENGTH && (
                                  <div
                                    className="list-group position-absolute w-100"
                                    style={{
                                      zIndex: 20,
                                      maxHeight: "240px",
                                      overflowY: "auto",
                                      top: "100%",
                                      left: 0,
                                    }}
                                  >
                                    {isFetchingSuppliers ? (
                                      <div className="list-group-item text-muted">
                                        Searching...
                                      </div>
                                    ) : supplierSearchResults.length === 0 ? (
                                      <div className="list-group-item text-muted">
                                        No suppliers found
                                      </div>
                                    ) : (
                                      supplierSearchResults.map((s) => {
                                        const sid = getSupplierId(s);
                                        const label = getSupplierLabel(s);
                                        return (
                                          <button
                                            key={sid || label}
                                            type="button"
                                            className="list-group-item list-group-item-action"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              handleSelectSupplier(s);
                                            }}
                                          >
                                            {label || sid || "Unnamed supplier"}
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                            </div>
                            <small className="form-text text-muted">
                              Type at least {SUPPLIER_SEARCH_MIN_LENGTH}{" "}
                              characters to search suppliers by name.
                            </small>
                          </div>
                        </div>
                      )}
                      {role === "customer" && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Customer<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <div
                              className="position-relative"
                              ref={customerSearchPanelRef}
                            >
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Search customer by name..."
                                  value={customerSearch}
                                  onChange={handleCustomerSearchChange}
                                  onKeyPress={handleCustomerSearchKeyPress}
                                  onFocus={() => {
                                    if (customerSearchResults.length > 0) {
                                      setShowCustomerDropdown(true);
                                    }
                                  }}
                                  disabled={isLoading}
                                  autoComplete="off"
                                  required={!customerId}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={handleCustomerSearchTrigger}
                                    disabled={isLoading || isFetchingCustomers}
                                  >
                                    {isFetchingCustomers ? (
                                      <i className="fa-solid fa-spinner fa-spin" />
                                    ) : (
                                      <i className="fa-solid fa-search" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {showCustomerDropdown &&
                                customerSearch.trim().length >=
                                  CUSTOMER_SEARCH_MIN_LENGTH && (
                                  <div
                                    className="list-group position-absolute w-100"
                                    style={{
                                      zIndex: 20,
                                      maxHeight: "240px",
                                      overflowY: "auto",
                                      top: "100%",
                                      left: 0,
                                    }}
                                  >
                                    {isFetchingCustomers ? (
                                      <div className="list-group-item text-muted">
                                        Searching...
                                      </div>
                                    ) : customerSearchResults.length === 0 ? (
                                      <div className="list-group-item text-muted">
                                        No customers found
                                      </div>
                                    ) : (
                                      customerSearchResults.map((c) => {
                                        const cid = getCustomerId(c);
                                        const label = getCustomerLabel(c);
                                        return (
                                          <button
                                            key={cid || label}
                                            type="button"
                                            className="list-group-item list-group-item-action"
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              handleSelectCustomer(c);
                                            }}
                                          >
                                            {label || cid || "Unnamed customer"}
                                          </button>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                            </div>
                            <small className="form-text text-muted">
                              Type at least {CUSTOMER_SEARCH_MIN_LENGTH}{" "}
                              characters to search customers by name.
                            </small>
                          </div>
                        </div>
                      )}
                      {useSupplierContactSelection && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Contact<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <div
                              className="table-responsive"
                              style={{
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                              }}
                            >
                              <table className="table table-sm mb-0">
                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                  <tr>
                                    <th style={{ width: "42px" }} />
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {supplierContacts.map((contact, index) => {
                                    const contactKey = getContactKey(
                                      contact,
                                      index,
                                    );
                                    const isSelected =
                                      selectedContactKeys.has(contactKey);

                                    return (
                                      <tr
                                        key={contactKey}
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          handleContactRowClick(
                                            contactKey,
                                            contact,
                                            isSelected,
                                          )
                                        }
                                      >
                                        <td className="align-middle">
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleContactToggle(
                                                contactKey,
                                                contact,
                                                e.target.checked,
                                              );
                                            }}
                                            disabled={isLoading}
                                          />
                                        </td>
                                        <td className="align-middle">
                                          {contact.contactPerson || "Contact"}
                                        </td>
                                        <td className="align-middle">
                                          {contact.email || "-"}
                                        </td>
                                        <td className="align-middle">
                                          {contact.phonenumber || "-"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <small className="form-text text-muted">
                              Select one or more contacts. Fill or update the
                              details below for each selected contact before
                              inviting.
                            </small>
                          </div>
                        </div>
                      )}
                      {useSupplierContactSelection &&
                        getSelectedInviteEntries().map((entry, index) => {
                          const sourceContact = supplierContacts.find(
                            (contact, contactIndex) =>
                              getContactKey(contact, contactIndex) ===
                              entry.contactKey,
                          );
                          const contactLabel =
                            sourceContact?.contactPerson ||
                            `Contact ${index + 1}`;

                          return (
                            <div
                              key={entry.contactKey}
                              className="form-group row"
                            >
                              <div className="col-12 col-md-8 offset-md-4">
                                <div
                                  className="p-3 mb-3"
                                  style={{
                                    border: "1px solid #dee2e6",
                                    borderRadius: "4px",
                                    backgroundColor: "#f8fbff",
                                  }}
                                >
                                  <div
                                    className="font-weight-bold mb-3"
                                    style={{ color: "#007bff" }}
                                  >
                                    {contactLabel}
                                  </div>
                                  <div className="form-group">
                                    <label>
                                      Name<span className="f_red">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Name"
                                      value={entry.name}
                                      onChange={(e) =>
                                        handleContactDraftChange(
                                          entry.contactKey,
                                          "name",
                                          e.target.value,
                                        )
                                      }
                                      disabled={isLoading}
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>
                                      Email<span className="f_red">*</span>
                                    </label>
                                    <input
                                      type="email"
                                      className="form-control"
                                      placeholder="Email"
                                      value={entry.email}
                                      onChange={(e) =>
                                        handleContactDraftChange(
                                          entry.contactKey,
                                          "email",
                                          e.target.value,
                                        )
                                      }
                                      disabled={isLoading}
                                    />
                                  </div>
                                  <div className="form-group mb-0">
                                    <label>Phone Number</label>
                                    <input
                                      type="tel"
                                      className="form-control"
                                      placeholder="Phone Number"
                                      value={entry.phoneNumber}
                                      onChange={(e) => {
                                        const value = e.target.value
                                          .replace(/\D/g, "")
                                          .slice(0, 10);
                                        handleContactDraftChange(
                                          entry.contactKey,
                                          "phoneNumber",
                                          value,
                                        );
                                      }}
                                      onKeyPress={(e) => {
                                        if (
                                          !/[0-9]/.test(e.key) &&
                                          e.key !== "Backspace" &&
                                          e.key !== "Delete" &&
                                          e.key !== "Tab"
                                        ) {
                                          e.preventDefault();
                                        }
                                      }}
                                      maxLength={10}
                                      disabled={isLoading}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {!useSupplierContactSelection &&
                        !useCustomerContactSelection && (
                          <>
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">
                                Name<span className="f_red">*</span>
                              </label>
                              <div className="col-12 col-md-8">
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Name"
                                  value={name}
                                  onChange={(e) => setName(e.target.value)}
                                  required
                                  disabled={isLoading}
                                />
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">
                                Email<span className="f_red">*</span>
                              </label>
                              <div className="col-12 col-md-8">
                                <input
                                  type="email"
                                  className="form-control"
                                  placeholder="Email"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  required
                                  disabled={isLoading}
                                />
                              </div>
                            </div>
                            <div className="form-group row">
                              <label className="col-12 col-md-4 col-form-label">
                                Phone Number<span className="f_red">*</span>
                              </label>
                              <div className="col-12 col-md-8">
                                <input
                                  type="tel"
                                  className="form-control"
                                  placeholder="Phone Number"
                                  value={phoneNumber}
                                  onChange={(e) => {
                                    const value = e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 10);
                                    setPhoneNumber(value);
                                  }}
                                  onKeyPress={(e) => {
                                    if (
                                      !/[0-9]/.test(e.key) &&
                                      e.key !== "Backspace" &&
                                      e.key !== "Delete" &&
                                      e.key !== "Tab"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                  maxLength={10}
                                  required
                                  disabled={isLoading}
                                />
                              </div>
                            </div>
                          </>
                        )}

                      {useCustomerContactSelection && (
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Contact<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <div
                              className="table-responsive"
                              style={{
                                border: "1px solid #dee2e6",
                                borderRadius: "4px",
                              }}
                            >
                              <table className="table table-sm mb-0">
                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                  <tr>
                                    <th style={{ width: "42px" }} />
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customerContacts.map((contact, index) => {
                                    const contactKey = getContactKey(
                                      contact,
                                      index,
                                    );
                                    const isSelected =
                                      selectedCustomerContactKeys.has(
                                        contactKey,
                                      );

                                    return (
                                      <tr
                                        key={contactKey}
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                          handleCustomerContactRowClick(
                                            contactKey,
                                            contact,
                                            isSelected,
                                          )
                                        }
                                      >
                                        <td className="align-middle">
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                              e.stopPropagation();
                                              handleCustomerContactToggle(
                                                contactKey,
                                                contact,
                                                e.target.checked,
                                              );
                                            }}
                                            disabled={isLoading}
                                          />
                                        </td>
                                        <td className="align-middle">
                                          {contact.contactPerson || "Contact"}
                                        </td>
                                        <td className="align-middle">
                                          {contact.email || "-"}
                                        </td>
                                        <td className="align-middle">
                                          {contact.contactNumber || "-"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            <small className="form-text text-muted">
                              Select one or more contacts. Fill or update the
                              details below for each selected contact before
                              inviting.
                            </small>
                          </div>
                        </div>
                      )}
                      {useCustomerContactSelection &&
                        getSelectedCustomerInviteEntries().map(
                          (entry, index) => {
                            const sourceContact = customerContacts.find(
                              (contact, contactIndex) =>
                                getContactKey(contact, contactIndex) ===
                                entry.contactKey,
                            );
                            const contactLabel =
                              sourceContact?.contactPerson ||
                              `Contact ${index + 1}`;

                            return (
                              <div
                                key={entry.contactKey}
                                className="form-group row"
                              >
                                <div className="col-12 col-md-8 offset-md-4">
                                  <div
                                    className="p-3 mb-3"
                                    style={{
                                      border: "1px solid #dee2e6",
                                      borderRadius: "4px",
                                      backgroundColor: "#f8fbff",
                                    }}
                                  >
                                    <div
                                      className="font-weight-bold mb-3"
                                      style={{ color: "#007bff" }}
                                    >
                                      {contactLabel}
                                    </div>
                                    <div className="form-group">
                                      <label>
                                        Name<span className="f_red">*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Name"
                                        value={entry.name}
                                        onChange={(e) =>
                                          handleCustomerContactDraftChange(
                                            entry.contactKey,
                                            "name",
                                            e.target.value,
                                          )
                                        }
                                        disabled={isLoading}
                                      />
                                    </div>
                                    <div className="form-group">
                                      <label>
                                        Email<span className="f_red">*</span>
                                      </label>
                                      <input
                                        type="email"
                                        className="form-control"
                                        placeholder="Email"
                                        value={entry.email}
                                        onChange={(e) =>
                                          handleCustomerContactDraftChange(
                                            entry.contactKey,
                                            "email",
                                            e.target.value,
                                          )
                                        }
                                        disabled={isLoading}
                                      />
                                    </div>
                                    <div className="form-group mb-0">
                                      <label>Phone Number</label>
                                      <input
                                        type="tel"
                                        className="form-control"
                                        placeholder="Phone Number"
                                        value={entry.phoneNumber}
                                        onChange={(e) => {
                                          const value = e.target.value
                                            .replace(/\D/g, "")
                                            .slice(0, 10);
                                          handleCustomerContactDraftChange(
                                            entry.contactKey,
                                            "phoneNumber",
                                            value,
                                          );
                                        }}
                                        onKeyPress={(e) => {
                                          if (
                                            !/[0-9]/.test(e.key) &&
                                            e.key !== "Backspace" &&
                                            e.key !== "Delete" &&
                                            e.key !== "Tab"
                                          ) {
                                            e.preventDefault();
                                          }
                                        }}
                                        maxLength={10}
                                        disabled={isLoading}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          },
                        )}
                    </div>
                    <div className="card-footer text-center">
                      <button
                        type="submit"
                        className="btn btn-blue"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <PulseLoader color="#ffffff" size={8} />
                        ) : selectedContactCount > 1 ? (
                          `Invite (${selectedContactCount})`
                        ) : selectedCustomerContactCount > 1 ? (
                          `Invite (${selectedCustomerContactCount})`
                        ) : (
                          "Invite"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAdd;
